import QRCode from 'qrcode';
import { FormEvent, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { useAuthStore } from '@/stores/authStore';

type SetupStep = 'generate' | 'verify' | 'done';

export function MfaSetupPage() {
  const { mfaEnabled, setupTotp, verifyTotp } = useAuthStore();
  const [step, setStep] = useState<SetupStep>('generate');
  const [qrDataUrl, setQrDataUrl] = useState<string | null>(null);
  const [secretKey, setSecretKey] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const shouldGenerate = !mfaEnabled && step === 'generate';

  useEffect(() => {
    if (!shouldGenerate) return;

    let cancelled = false;

    (async () => {
      await Promise.resolve();
      if (cancelled) return;
      setIsLoading(true);
      setError(null);
      try {
        const totpDetails = await setupTotp();
        if (cancelled) return;
        const uri = totpDetails.getSetupUri('Yarn Tales Admin');
        const dataUrl = await QRCode.toDataURL(uri.toString(), { width: 256, margin: 2 });
        if (cancelled) return;
        setQrDataUrl(dataUrl);
        setSecretKey(totpDetails.sharedSecret);
        setStep('verify');
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : 'Failed to generate TOTP setup');
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [shouldGenerate, setupTotp]);

  const handleVerify = async (e: FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    try {
      await verifyTotp(code);
      setQrDataUrl(null);
      setSecretKey(null);
      setStep('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid verification code');
    } finally {
      setIsLoading(false);
    }
  };

  if (mfaEnabled && step !== 'done') {
    return (
      <div>
        <h1 className="text-2xl font-bold text-slate-900 mb-6">MFA Setup</h1>
        <div className="rounded-lg border border-slate-200 bg-white p-8 max-w-lg">
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">MFA is already configured</h2>
            <p className="text-slate-600 mb-6">
              Your account is secured with multi-factor authentication.
            </p>
            <Link
              to="/"
              className="inline-block rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-slate-900 mb-6">MFA Setup</h1>

      <div className="rounded-lg border border-slate-200 bg-white p-8 max-w-lg">
        {error && <div className="mb-6 rounded-lg bg-red-50 p-4 text-sm text-red-700">{error}</div>}

        {step === 'generate' && isLoading && (
          <div className="text-center py-12">
            <div className="h-8 w-8 animate-spin rounded-full border-4 border-indigo-600 border-t-transparent mx-auto mb-4"></div>
            <p className="text-slate-600">Setting up authenticator...</p>
          </div>
        )}

        {step === 'verify' && (
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">Scan QR Code</h2>
            <p className="text-sm text-slate-600 mb-6">
              Scan this QR code with your authenticator app (e.g., Google Authenticator, Authy),
              then enter the 6-digit code below.
            </p>

            {qrDataUrl && (
              <div className="flex justify-center mb-6">
                <img
                  src={qrDataUrl}
                  alt="TOTP QR Code"
                  className="rounded-lg"
                  width={256}
                  height={256}
                />
              </div>
            )}

            {secretKey && (
              <div className="mb-6 rounded-lg bg-slate-50 p-4">
                <p className="text-xs font-medium text-slate-500 mb-1">Manual entry key</p>
                <p className="font-mono text-sm text-slate-900 break-all select-all">{secretKey}</p>
              </div>
            )}

            <form onSubmit={handleVerify} className="space-y-4">
              <div>
                <label htmlFor="totp-code" className="block text-sm font-medium text-slate-700">
                  Verification code
                </label>
                <input
                  id="totp-code"
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  autoComplete="one-time-code"
                  autoFocus
                  className="mt-1 block w-full rounded-lg border border-slate-300 px-4 py-2 text-center text-2xl tracking-widest focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/20"
                  placeholder="000000"
                />
              </div>

              <button
                type="submit"
                disabled={isLoading || code.length !== 6}
                className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed transition-colors"
              >
                {isLoading ? 'Verifying...' : 'Verify & Activate MFA'}
              </button>
            </form>
          </div>
        )}

        {step === 'done' && (
          <div className="text-center">
            <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-green-100">
              <svg
                className="h-6 w-6 text-green-600"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                />
              </svg>
            </div>
            <h2 className="text-lg font-semibold text-slate-900 mb-2">MFA Activated</h2>
            <p className="text-slate-600 mb-6">
              Multi-factor authentication has been enabled for your account. You will be prompted
              for a code on future sign-ins.
            </p>
            <Link
              to="/"
              className="inline-block rounded-lg bg-indigo-600 px-4 py-2 text-white font-medium hover:bg-indigo-700 transition-colors"
            >
              Back to Dashboard
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
